import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';

export interface ServiceZonePointPayload {
  lat: number;
  lng: number;
  sequence?: number;
}

export interface ServiceZonePolygonAreaDto {
  id: string;
  name: string;
  grade: string;
  enabled: boolean;
  sortOrder: number;
  points: { lat: number; lng: number; sequence: number }[];
}

export interface ZoneOperatingHour {
  day: number; // 0 = Sunday … 6 = Saturday
  open: string; // HH:mm
  close: string; // HH:mm
  isClosed: boolean;
}

/** Operating hours plus IANA timezone used to interpret open/close times. */
export interface ZoneOperatingHoursResponse {
  hours: ZoneOperatingHour[];
  /** e.g. Asia/Kolkata — derived from zone polygon centroid */
  timezone?: string;
}

export interface ServiceZoneDto {
  id: string;
  name: string;
  enabled: boolean;
  sortOrder: number;
  areas: ServiceZonePolygonAreaDto[];
  operatingHours?: ZoneOperatingHour[];
  /** IANA timezone for this zone's operating hours */
  timezone?: string;
}

export interface ServiceZoneAreaPayload {
  id?: string;
  name: string;
  grade: string;
  enabled?: boolean;
  sortOrder?: number;
  points: { lat: number; lng: number }[];
}

export interface CreateServiceZonePayload {
  name: string;
  enabled?: boolean;
  sortOrder?: number;
  /** Preferred: one or more graded polygons */
  areas?: ServiceZoneAreaPayload[];
  /** @deprecated Wrapped as one area with grade `default` */
  points?: { lat: number; lng: number }[];
}

export interface UpdateServiceZonePayload {
  name?: string;
  enabled?: boolean;
  sortOrder?: number;
  areas?: ServiceZoneAreaPayload[];
  /** @deprecated Wrapped as one area with grade `default` */
  points?: { lat: number; lng: number }[];
}

@Injectable({ providedIn: 'root' })
export class ApiServiceZonesService {
  private api = inject(ApiService);

  list(options?: { includeOperatingHours?: boolean }): Observable<ServiceZoneDto[]> {
    const params =
      options?.includeOperatingHours === false ? { includeOperatingHours: false } : undefined;
    return this.api.get<ServiceZoneDto[]>('service-zones', params);
  }

  create(body: CreateServiceZonePayload): Observable<ServiceZoneDto> {
    return this.api.post<ServiceZoneDto>('service-zones', body);
  }

  update(id: string, body: UpdateServiceZonePayload): Observable<ServiceZoneDto> {
    return this.api.patch<ServiceZoneDto>(`service-zones/${id}`, body);
  }

  remove(id: string): Observable<{ success: boolean }> {
    return this.api.delete<{ success: boolean }>(`service-zones/${id}`);
  }

  getOperatingHours(id: string): Observable<ZoneOperatingHoursResponse> {
    return this.api
      .get<ZoneOperatingHoursResponse | ZoneOperatingHour[]>(`service-zones/${id}/operating-hours`)
      .pipe(map((res) => (Array.isArray(res) ? { hours: res } : res)));
  }

  upsertOperatingHours(id: string, hours: ZoneOperatingHour[]): Observable<ZoneOperatingHour[]> {
    return this.api.put<ZoneOperatingHour[]>(`service-zones/${id}/operating-hours`, { hours });
  }
}
